/**
 * SessionsPage (iter203c) — admin view of every active user_sessions
 * row. Lets an admin revoke a single session or every session for a
 * given email. Filterable by role + search.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users, ArrowClockwise, Trash, SignOut, MagnifyingGlass } from "@phosphor-icons/react";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";

const ROLE_OPTIONS = [
  { v: "",         label: "All" },
  { v: "staff",    label: "Staff" },
  { v: "customer", label: "Customer" },
];

function fmtTs(v) {
  if (!v) return "—";
  try {
    const d = new Date(v.endsWith?.("Z") ? v : `${v}Z`);
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).format(d);
  } catch { return String(v); }
}

export default function SessionsPage({ user, onLogout, onLogoutAll }) {
  const [items, setItems] = useState([]);
  const [role, setRole] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyToken, setBusyToken] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (role) params.role = role;
      if (search.trim()) params.search = search.trim();
      const r = await api.get("/admin/sessions", { params });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load sessions");
    } finally { setLoading(false); }
  }, [role, search]);

  useEffect(() => { refresh(); }, [refresh]);

  const revokeOne = async (row) => {
    if (!window.confirm(`Revoke session ${row.session_prefix} for ${row.email}?`)) return;
    setBusyToken(row.session_token);
    try {
      await api.delete(`/admin/sessions/${encodeURIComponent(row.session_token)}`);
      toast.success(`Revoked 1 session for ${row.email}`);
      setItems(prev => prev.filter(x => x.session_token !== row.session_token));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    } finally { setBusyToken(""); }
  };

  const revokeAllForUser = async (email) => {
    if (!window.confirm(`Sign ${email} out of every device? This cannot be undone.`)) return;
    setBusyToken(`all-${email}`);
    try {
      const r = await api.post("/admin/sessions/revoke-user", { email });
      toast.success(`Revoked ${r.data.revoked} session(s) for ${email}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    } finally { setBusyToken(""); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white"
         data-testid="sessions-page">
      <AdminPageHeader
        icon={<Users size={20} weight="fill" className="text-blue-700" />}
        title="Active Sessions"
        subtitle="Signed-in users right now — revoke individually or per-user"
        user={user} onLogout={onLogout} onLogoutAll={onLogoutAll}
        testId="sessions-back"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-sm p-3">
          {ROLE_OPTIONS.map(o => (
            <button key={o.v} onClick={() => setRole(o.v)}
                    data-testid={`sessions-role-${o.v || "all"}`}
                    className={`px-2.5 py-1 rounded-sm text-xs font-bold uppercase tracking-wider border transition
                      ${role === o.v
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
              {o.label}
            </button>
          ))}
          <div className="h-6 w-px bg-slate-200" />
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={14} weight="bold"
              className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search}
                   onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search email…"
                   data-testid="sessions-search"
                   className="w-full h-8 pl-7 pr-2 text-xs rounded-sm border border-slate-200 bg-white" />
          </div>
          <button onClick={refresh} disabled={loading}
                  data-testid="sessions-refresh"
                  className="h-8 px-3 rounded-sm border text-xs font-bold uppercase tracking-wider
                             bg-white text-slate-700 border-slate-200 hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
            <ArrowClockwise size={12} weight="bold" /> Refresh
          </button>
        </div>

        <div className="text-xs text-slate-500" data-testid="sessions-count">
          {loading ? "Loading…" : `${items.length.toLocaleString()} active session(s)`}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs" data-testid="sessions-table">
            <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2 hidden sm:table-cell">Provider</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Created</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Expires</th>
                <th className="text-left px-3 py-2 hidden lg:table-cell">Session</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr><td colSpan={7} className="text-center py-8 text-slate-400"
                        data-testid="sessions-empty">
                  No active sessions match these filters.
                </td></tr>
              )}
              {items.map((it) => (
                <tr key={it.session_token}
                    className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-3 py-2 font-medium text-slate-800">{it.email || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-1.5 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-wider
                      ${it.role === "customer"
                        ? "bg-purple-50 text-purple-700 border-purple-200"
                        : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                      {it.role || "staff"}
                    </span>
                  </td>
                  <td className="px-3 py-2 hidden sm:table-cell text-slate-600">
                    {it.oauth_provider || (it.role === "customer" ? "password" : "—")}
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell font-mono text-[11px] text-slate-500">{fmtTs(it.created_at)}</td>
                  <td className="px-3 py-2 hidden md:table-cell font-mono text-[11px] text-slate-500">{fmtTs(it.expires_at)}</td>
                  <td className="px-3 py-2 hidden lg:table-cell font-mono text-[10px] text-slate-400">{it.session_prefix}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => revokeOne(it)}
                            disabled={busyToken === it.session_token}
                            data-testid={`sessions-revoke-one-${it.session_prefix}`}
                            title="Revoke this session"
                            className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border text-[10px] font-bold uppercase tracking-wider
                                       bg-white text-red-700 border-red-200 hover:bg-red-50 disabled:opacity-50">
                      <Trash size={11} weight="bold" /> Revoke
                    </button>
                    <button onClick={() => revokeAllForUser(it.email)}
                            disabled={!it.email || busyToken === `all-${it.email}`}
                            data-testid={it.email ? `sessions-revoke-all-${it.email}` : "sessions-revoke-all-empty"}
                            title="Revoke every session for this user"
                            hidden={!it.email}
                            className="ml-1 inline-flex items-center gap-1 h-7 px-2 rounded-sm border text-[10px] font-bold uppercase tracking-wider
                                       bg-white text-slate-700 border-slate-200 hover:bg-slate-50 disabled:opacity-50">
                      <SignOut size={11} weight="bold" /> All
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
